import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import heroBg from "@/assets/hero-bg.jpg";
import { useContactDialog } from "./ContactTrigger";
import { motion } from "framer-motion";
import { useParallax } from "@/hooks/useParallax";
import { useLanguage } from "@/contexts/LanguageContext";

const Hero = () => {
  const { openDialog } = useContactDialog();
  const parallaxRef = useParallax(0.3);
  const { t } = useLanguage();
  // Defer animations to reduce TBT
  const [animationsReady, setAnimationsReady] = useState(false);
  
  useEffect(() => {
    // Wait for idle time before enabling animations
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setAnimationsReady(true), { timeout: 100 });
    } else {
      setTimeout(() => setAnimationsReady(true), 50);
    }
  }, []);

  return (
    <header className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      {/* Animated gradient background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5 animate-gradient-shift" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float-slow-reverse" />
        </div>
      </div>

      {/* Background Image with Parallax - using CSS transforms directly */}
      <div 
        ref={parallaxRef}
        className="absolute inset-0 z-0"
      >
        <img
          src={heroBg}
          alt="Corporate innovation meeting with startup founders discussing technology partnership and proof of concept development"
          className="w-full h-full object-cover opacity-5 scale-110"
          decoding="async"
          fetchPriority="high"
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-24 relative z-10">
        <div className="max-w-7xl mx-auto space-y-12">
          <motion.div
            initial={animationsReady ? { opacity: 0, y: 20 } : false}
            animate={animationsReady ? { opacity: 1, y: 0 } : false}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-sm md:text-base font-semibold tracking-widest text-primary uppercase mb-8"
          >
            {t("hero.subtitle")}
          </motion.div>
          
          <motion.h1
            initial={animationsReady ? { opacity: 0, y: 30 } : false}
            animate={animationsReady ? { opacity: 1, y: 0 } : false}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-black tracking-tighter leading-none"
          >
            {t("hero.title").split('\n').map((line, i) => (
              <span key={i}>
                {line.split(' ').map((word, j) => (
                  <span key={j} className={`inline-block hover:scale-105 transition-transform duration-300 ${j === line.split(' ').length - 1 ? 'text-gradient' : ''}`}>
                    {word}{j < line.split(' ').length - 1 ? ' ' : ''}
                  </span>
                ))}
                {i === 0 && <br />}
              </span>
            ))}
          </motion.h1>

          <motion.div
            initial={animationsReady ? { opacity: 0, y: 20 } : false}
            animate={animationsReady ? { opacity: 1, y: 0 } : false}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="max-w-2xl space-y-6"
          >
            <p className="text-xl md:text-2xl text-foreground leading-relaxed font-medium">
              {t("hero.description")}
            </p>
          </motion.div>

          <motion.div
            initial={animationsReady ? { opacity: 0, y: 20 } : false}
            animate={animationsReady ? { opacity: 1, y: 0 } : false}
            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
            className="flex flex-col sm:flex-row gap-6 pt-8"
          >
            <Button
              size="lg"
              onClick={openDialog}
              className="text-base px-10 py-7 font-semibold group hover:scale-105 transition-all duration-300"
            >
              {t("hero.cta")}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
              className="text-base px-10 py-7 font-semibold hover:scale-105 transition-all duration-300"
            >
              {t("hero.learn")}
            </Button>
          </motion.div>
        </div>
      </div>
    </header>
  );
};

export default Hero;
